<template>
  <article class="tw:bg-gray-100 tw:rounded-xl tw:p-3 tw:flex tw:flex-col tw:md:flex-row tw:gap-4">
    <div class="tw:min-w-0 tw:grow">
      <div class="tw:mb-3 tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-start tw:md:justify-between">
        <div class="tw:flex tw:flex-col">
          <h3 class="tw:text-base tw:font-bold">{{ languageLabel }}</h3>
          <p class="tw:text-sm tw:text-gray-700">
            {{ sourceLabel }}
          </p>
        </div>
      </div>

      <p
        ref="descriptionBodyRef"
        class="tw:text-start"
        :dir="descriptionTextDirection"
        :class="isShowingMore ? 'tw:line-clamp-none' : 'tw:line-clamp-3'">
        {{ description.description }}
      </p>
      <button
        v-if="isClamped"
        type="button"
        class="tw:text-sm tw:text-blue-700 tw:font-bold tw:cursor-pointer"
        data-testid="property-description-toggle"
        :aria-expanded="isShowingMore"
        @click="isShowingMore = !isShowingMore">
        {{ isShowingMore ? $t('show_less_info') : $t('show_more') }}
      </button>
    </div>
    <GhostButton
      size="medium"
      class="tw:shrink-0 tw:min-w-[90px]"
      data-testid="property-description-edit"
      @click="emit('edit', description)">
      {{ $t('app_description_edit') }}
    </GhostButton>
  </article>
</template>

<script setup>
import { computed, ref, onMounted, watch, nextTick } from 'vue'

import {
  DESCRIPTION_SOURCE,
  usePropertyDescriptions,
} from '@/components/propertyDetails/propertyDescriptions/usePropertyDescriptions.js'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import { useFormatting } from '@/composables/useFormatting'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { formatDate } = useFormatting()
const { getLanguageLabelByCode, getDescriptionTextDirection } = usePropertyDescriptions()
const emit = defineEmits(['edit'])

const props = defineProps({
  description: {
    type: Object,
    required: true,
  },
})

const isShowingMore = ref(false)

const languageLabel = computed(() => getLanguageLabelByCode(props.description.languageCode))
const descriptionTextDirection = computed(() => getDescriptionTextDirection(props.description.languageCode))

const sourceLabel = computed(() => {
  if (props.description.source === DESCRIPTION_SOURCE.HOTELIER) {
    return t('description_updated_date', {
      UpdatedDate: formatDate(props.description.modifiedAt, {
        timeZone: 'UTC',
      }),
    })
  }

  return t('app_description_freelancer')
})

/*
 * This is a workaround to determine if the description body text is clamped or not.
 * It is used to determine if the "Show more" button should be shown or not.
 */
const descriptionBodyRef = ref(null)
const isClamped = ref(false)
const updateClampState = async () => {
  await nextTick()
  const el = descriptionBodyRef.value
  if (!el) return

  isClamped.value = el.scrollHeight > el.clientHeight
}
watch(() => props.description.description, updateClampState)

onMounted(updateClampState)
</script>
